/**
 * The first-visit intro tour's pages and rules on the web (ISSUE-1406), with
 * no renderer. The copy is the phone's, word for word
 * (`apps/mobile/src/welcome/introTourModel.ts`), so a fan meets the same
 * three pages on both: Live scores, Run your pools, Talk it over. v1:
 * lib/feature/auth/ui/page/onboarding_screen.dart, Skip until the last page,
 * Next then Get started, and the sign-in link pinned on every page.
 */
export type typeIntroTone = "primary" | "accent" | "success";

export type typeIntroPage = {
  key: "scores" | "pools" | "chat";
  title: string;
  body: string;
  tone: typeIntroTone;
};

const FIRST_PAGE: typeIntroPage = {
  key: "scores",
  title: "Live scores",
  body: "Follow every game as it happens, with scores, game status and final results in one place.",
  tone: "primary",
};

export const INTRO_PAGES: readonly typeIntroPage[] = [
  FIRST_PAGE,
  {
    key: "pools",
    title: "Run your pools",
    body: "Start a pool for your crew in seconds, make your picks, and watch the standings move.",
    tone: "accent",
  },
  {
    key: "chat",
    title: "Talk it over",
    body: "Message your pool, talk strategy, and celebrate the big plays without leaving the app.",
    tone: "success",
  },
];

export const INTRO_TOUR_LABEL = "Welcome to Sportsmet";
export const INTRO_SKIP_LABEL = "Skip";
export const INTRO_NEXT_LABEL = "Next";
export const INTRO_GET_STARTED_LABEL = "Get started";
export const INTRO_SIGN_IN_LABEL = "I already have an account";

/**
 * How far a finger has to travel sideways before a touch counts as a swipe.
 * Shorter than this is a tap or a wobble, not a request to change pages.
 */
export const INTRO_SWIPE_MIN_PX = 48;

export function isLastIntroPage(index: number): boolean {
  return index >= INTRO_PAGES.length - 1;
}

/** Keeps a requested page inside the tour. */
export function clampIntroPage(index: number): number {
  return Math.min(Math.max(index, 0), INTRO_PAGES.length - 1);
}

/** The page at an index, kept inside the tour. */
export function introPageAt(index: number): typeIntroPage {
  return INTRO_PAGES[clampIntroPage(index)] ?? FIRST_PAGE;
}

/** "Page 2 of 3", for the dots a screen reader cannot see. */
export function introProgressLabel(index: number): string {
  return `Page ${index + 1} of ${INTRO_PAGES.length}`;
}

/**
 * The page a finished touch asks for, or null when it was not a swipe: too
 * short, or more up and down than sideways (the fan was scrolling). A swipe
 * to the left moves forward, the way a phone pager does.
 */
export function introSwipeTarget(input: {
  index: number;
  deltaX: number;
  deltaY: number;
}): number | null {
  const { index, deltaX, deltaY } = input;
  if (Math.abs(deltaX) < INTRO_SWIPE_MIN_PX) return null;
  if (Math.abs(deltaX) <= Math.abs(deltaY)) return null;
  const next = clampIntroPage(deltaX < 0 ? index + 1 : index - 1);
  return next === index ? null : next;
}
